import axios from 'axios';

import { ApiPath } from '../constants/apiPath';
import { apiBase, apiKey } from '../constants/apiKey';
import { getAccessToken, getRefreshToken, storeTokens } from '../utils/token-store';

var isPop = false;
var isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

const processQueue = (token: string) => {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
};

const axiosInstance = axios.create({
  baseURL: apiBase,
  timeout: 10000,
});

axiosInstance.interceptors.request.use(
  (config) => {
    config.headers['Content-Type'] = 'application/json';
    config.headers['Accept-Language'] = 'en';
    config.headers['ngrok-skip-browser-warning'] = 'true';
    config.headers['apikey'] = apiKey;

    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest?._retry) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          refreshQueue.push((newToken: string) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();
        if (!refreshToken) throw new Error('No refresh token');

        const response = await axios.post(
          apiBase + ApiPath.auth.refreshToken,
          {},
          {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
              apikey: import.meta.env.VITE_API_KEY,
            },
          }
        );

        const newAccessToken: string = response?.data?.data?.accessToken;
        const newRefreshToken: string = response?.data?.data?.refreshToken;

        storeTokens(newAccessToken, newRefreshToken);

        const accessToken = getAccessToken()!;
        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        processQueue(accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axiosInstance(originalRequest);
      } catch {
        localStorage.clear();
        if (!isPop) {
          isPop = true;
          window.location.href = '/login';
        }
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
